/**
 * habilleur-size-to-cart.js
 *
 * Voegt een product direct toe aan de winkelwagen wanneer een maat wordt
 * aangeklikt op de collectiepagina (product card).
 *
 * - Luistert in de capture-fase naar pointerdown én click op .card-size-option
 *   knoppen (maat-pills in de foto-overlay), vóórdat product-card's eigen
 *   "navigeer naar productpagina" handler kan afgaan.
 * - Kiest de variant die bij de geklikte maat hoort, en (indien aanwezig) de
 *   geselecteerde kleur op dezelfde productkaart.
 * - Voert een AJAX add-to-cart uit via het echte Theme.routes.cart_add_url endpoint
 * - Toont visuele feedback op de geklikte pill
 * - Dispatcht hetzelfde CartAddEvent dat de theme-eigen add-to-cart-component gebruikt,
 *   zodat de cart-drawer, cart-icon-bubble en cart-items-component allemaal netjes
 *   bijwerken zoals bij een normale "In winkelwagen"-knop.
 * - Opent de cart-drawer altijd expliciet (los van de "auto open" instelling),
 *   zodat je direct ziet dat het product is toegevoegd.
 *
 * De maten-balk zelf wordt server-side gerenderd als overlay op de productfoto
 * (zie snippets/card-size-overlay.liquid, gerenderd vanuit blocks/_product-card-gallery.liquid),
 * dus dit bestand hoeft niets meer in de DOM te verplaatsen.
 */

import { CartAddEvent } from '@theme/events';

(function () {
  'use strict';

  /** @type {WeakSet<HTMLElement>} */
  const recentAdds = new WeakSet();

  /**
   * Voeg variant toe aan winkelwagen via AJAX en meld dit aan de rest van de theme.
   * @param {string} variantId
   * @param {HTMLElement} triggerEl - het element dat geklikt werd
   * @param {string} [productId]
   */
  async function addVariantToCart(variantId, triggerEl, productId) {
    if (!variantId) return;

    const cartAddUrl = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        console.warn('[Habilleur] Cart add failed:', errData.description || response.status);
        showFeedback(triggerEl, false);
        return;
      }

      // Visuele feedback op de maat-pill
      showFeedback(triggerEl, true);

      // Haal de actuele (totale) hoeveelheid in de cart op, zodat de cart-bubble
      // het juiste aantal toont (i.p.v. altijd "1").
      let itemCount;
      try {
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        itemCount = cart.item_count;
      } catch (err) {
        // Geen probleem, de rest van de theme kan de cart-icon dan zelf verversen.
      }

      // Dit is hetzelfde event dat de normale "In winkelwagen"-knoppen dispatchen.
      // De cart-drawer, cart-items-component en cart-icon-bubble luisteren hier al naar.
      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'habilleur-size-to-cart',
          productId: productId,
          itemCount: itemCount,
        })
      );

      // Open de cart-drawer altijd expliciet, ongeacht de "automatisch openen"-instelling,
      // zodat je direct visueel bevestiging krijgt dat de maat is toegevoegd.
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } catch (err) {
      console.warn('[Habilleur] Fout bij toevoegen aan winkelwagen:', err);
      showFeedback(triggerEl, false);
    }
  }

  /**
   * Toon visuele feedback op de maat-pill
   * @param {HTMLElement} el
   * @param {boolean} success
   */
  function showFeedback(el, success) {
    if (!el) return;
    el.classList.add(success ? 'size-added-to-cart' : 'size-add-error');
    setTimeout(() => el.classList.remove('size-added-to-cart', 'size-add-error'), 900);
  }

  /**
   * Zoek de variant die bij de geklikte maat hoort, plus de gekozen kleur op de kaart.
   * @param {HTMLElement} sizeEl
   * @returns {string | undefined}
   */
  function resolveVariantId(sizeEl) {
    const fallbackId = sizeEl.dataset.variantId;
    const sizeValue = (sizeEl.dataset.optionValue || sizeEl.textContent || '').trim();
    const card = sizeEl.closest('product-card');
    const jsonEl = sizeEl.closest('.card-sizes-wrapper')?.querySelector('script.card-size-variants');

    if (!card || !sizeValue || !(jsonEl instanceof HTMLScriptElement)) return fallbackId;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent || '[]');
    } catch (err) {
      return fallbackId;
    }

    if (!Array.isArray(variants) || variants.length === 0) return fallbackId;

    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      card.querySelector('input[type="radio"][name*="-swatch"]:checked')
    );
    const colorValue = checkedSwatch?.value?.trim();

    const match = variants.find((variant) => {
      const options = Array.isArray(variant.options) ? variant.options.map((opt) => String(opt)) : [];
      if (!options.includes(sizeValue)) return false;
      if (!colorValue) return true;
      return options.includes(colorValue);
    });

    if (match?.id) return String(match.id);
    return fallbackId;
  }

  /**
   * Event listener: pointerdown/click op maat-pill (.card-size-option).
   *
   * Capture-fase + stopPropagation, zodat product-card's eigen click-to-navigate
   * handler (bubble-fase) nooit wordt bereikt. Pointerdown vangt de actie al op
   * desktop collectiekaarten, nog vóór een hover/opacity-overgang de click kan slikken.
   *
   * @param {Event} event
   */
  function handleSizeActivate(event) {
    if (event instanceof PointerEvent && event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const sizeEl = /** @type {HTMLElement | null} */ (target.closest('.card-size-option'));
    if (!sizeEl) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (recentAdds.has(sizeEl)) return;
    recentAdds.add(sizeEl);
    setTimeout(() => recentAdds.delete(sizeEl), 500);

    const isAvailable = sizeEl.dataset.available !== 'false';
    if (!isAvailable) return;

    const variantId = resolveVariantId(sizeEl);
    const productId = sizeEl.dataset.productId;
    if (!variantId) return;

    addVariantToCart(variantId, sizeEl, productId);
  }

  document.addEventListener('pointerdown', handleSizeActivate, true);
  document.addEventListener('click', handleSizeActivate, true);

  /**
   * Event listener: radio button change op product card variant picker
   * Wanneer een maat wordt geselecteerd op de collectiekaart, voeg direct toe.
   *
   * Alleen actief als de radio een size-optie is (geen kleurswatch).
   */
  document.addEventListener('change', function (event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input.type !== 'radio' || !input.dataset.variantId) return;

    // Alleen op product cards (collectiepagina), niet op de productpagina zelf
    const productCard = input.closest('product-card');
    if (!productCard) return;

    // Sla kleurenswatches over (fieldset met variant-option--swatches klasse)
    const fieldset = input.closest('fieldset');
    if (!fieldset) return;
    if (fieldset.classList.contains('variant-option--swatches')) return;

    // Niet beschikbare varianten overslaan
    if (input.dataset.optionAvailable === 'false') return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const label = /** @type {HTMLElement | null} */ (input.closest('label'));
    if (!label) return;
    addVariantToCart(variantId, label);
  });

  /** Toon alle kleurswatches direct (geen verborgen overflow-slot) */
  function syncSwatches(root = document) {
    root.querySelectorAll('swatches-variant-picker-component overflow-list').forEach((list) => {
      if (list.getAttribute('disabled') === 'true') return;

      if (typeof list.showAll === 'function') {
        list.showAll();
      } else {
        list.setAttribute('disabled', 'true');
      }
    });
  }

  window.HabilleurSwatches = { sync: syncSwatches };

  syncSwatches();
  requestAnimationFrame(() => syncSwatches());
  setTimeout(() => syncSwatches(), 100);
  document.addEventListener('shopify:section:load', (event) => {
    syncSwatches(event.target instanceof Element ? event.target : document);
  });

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            syncSwatches(node);
          }
        });
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
